import RESOURCE from '../../../resource/Resource';
import {
  getFileExtension,
  transcodeOptions,
  VIDEO_TYPES,
  AUDIO_TYPES,
  DOCUMENT_TYPES,
} from './FileHelpers';

/**
 * Call upload(file) externally to start a file transfer.
 *
 * TODO: make this support multiple files
 * TODO: segment uploads
 */

// Create Upload Resources
const _createCdnFile = (file) =>
  new Promise((resolve, reject) => {
    RESOURCE.execute('upload', 'createFiles', {
      filenames: JSON.stringify([file.name]),
    }).then(resolve, reject);
  });

const _uploadToCdn = (onProgress, file, onXhrCreate) => (uploadUrl) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    // File uploaded
    xhr.upload.addEventListener('load', () => {
      resolve(uploadUrl);
    });
    xhr.upload.addEventListener('progress', (e) => {
      const percentComplete = Math.ceil((e.loaded / e.total) * 100);
      onProgress(percentComplete);
    });
    xhr.upload.addEventListener('error', reject);

    xhr.open('PUT', uploadUrl.uploadURL);

    xhr.setRequestHeader('Access-Control-Allow-Origin', '*');
    xhr.setRequestHeader(
      'Access-Control-Expose-Headers',
      'Access-Control-Allow-Origin'
    );
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');

    // Note:
    // There is no need to use FileReader.readAsDataURL to convert the File to a data URL
    // only to convert it back into a Blob. A File object is already a Blob and can be used in xhr.send().
    // Also FileReader.readAsDataURL will not work for large files like videos due to browsers limiting
    // the size of files that can be read into memory.
    xhr.overrideMimeType(file.type);
    xhr.send(file);

    // pass the XMLHttpRequest instance back so upload can be abort
    // TODO: think of a proper way to handle abort
    // (i.e. xhr.upload.addEventListener('abort', reject))
    if (onXhrCreate) {
      onXhrCreate(xhr);
    }
  });

const _finalizeUpload = (file, thumb, shouldTranscode) => (uploadUrl) =>
  new Promise((resolve, reject) => {
    let options = null;
    if (AUDIO_TYPES.indexOf(file.type) !== -1) {
      options = {
        audio: transcodeOptions.audio,
        shouldTranscode,
      };
    } else if (VIDEO_TYPES.indexOf(file.type) !== -1) {
      options = {
        video: transcodeOptions.video,
        shouldTranscode,
      };
      if (thumb) {
        options.thumb = thumb;
      }
    } else if (DOCUMENT_TYPES.indexOf(file.type) !== -1) {
      options = { document: transcodeOptions.document };
      if (thumb) {
        options.thumb = thumb;
      }
    }

    RESOURCE.execute('upload', 'finalizeUpload', {
      storageKey: uploadUrl.storageKey,
      options,
    }).then(() => {
      resolve({
        storageKey: uploadUrl.storageKey,
        url: uploadUrl.downloadURL,
        sourceURL: uploadUrl.sourceURL,
        filename: file.name,
        size: file.size,
        mimetype: file.type,
        extension: getFileExtension(file.name),
      });
    }, reject);
  });

// By default shouldTrancode = false because we don't need transcoding by Filestack except for audio at the moment.
// One use case is uploading raw video file for Qencode to transcode, in
// which case we need the upload collection item to be created instantly,
// without waiting for Filestack to trancode the upload item.
export const upload = (
  callbacks,
  { file = null, blob = null, thumbnail = null, shouldTranscode = false }
) =>
  _createCdnFile(file)
    .then((uploadUrls) => uploadUrls[0])
    .then(_uploadToCdn(callbacks.progress, blob || file, callbacks.onXhrCreate))
    .then(_finalizeUpload(file, thumbnail, shouldTranscode));

export const uploadFileAndThumbnail = (
  callbacks,
  { file, blob, thumbnail, shouldTranscode }
) => {
  if (thumbnail) {
    const totalSize = file.size + thumbnail.size;
    let uploading = 'thumbnail';
    const progress = (percent) => {
      let uploadPercent = 0;
      if (uploading === 'file' && percent === 100) {
        uploadPercent = 100;
      } else if (uploading === 'thumbnail') {
        uploadPercent = (percent * thumbnail.size) / totalSize;
      } else {
        uploadPercent = (percent * file.size + thumbnail.size) / totalSize;
      }
      callbacks.progress(uploadPercent);
    };

    return upload(
      {
        progress,
        onXhrCreate: callbacks.onXhrCreate || null,
      },
      {
        file: thumbnail,
      }
    ).then((thumb) => {
      uploading = 'file';
      return upload(
        {
          progress,
          onXhrCreate: callbacks.onXhrCreate || null,
        },
        { file, blob, thumbnail: thumb, shouldTranscode }
      ).then((metadata) => ({
        file: metadata,
        thumb,
      }));
    });
  } else {
    return upload(
      {
        progress: callbacks.progress,
        onXhrCreate: callbacks.onXhrCreate || null,
      },
      { file, blob, shouldTranscode }
    ).then((metadata) => ({
      file: metadata,
      thumb: null,
    }));
  }
};
